import { RateLimiter } from './rateLimiter';

const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;
const AUTH_ENDPOINT = "https://accounts.spotify.com/authorize";
const RESPONSE_TYPE = "token";
const SCOPE = "user-top-read";

const spotifyRateLimiter = new RateLimiter({
    requestsPerSecond: 10,
    maxConcurrent: 5,
    retryAttempts: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    shouldRetry: (error) => {
        if (error.name === 'AbortError') return false;
        if (!error.status) return true;
        return error.status === 429 || error.status === 503 || error.status === 502 || error.status >= 500;
    }
});

// Token management utilities
export const isTokenValid = () => {
    const token = localStorage.getItem('spotify_token');
    const expiresAt = localStorage.getItem('spotify_token_expires_at');

    if (!token || !expiresAt) return false;
    return Date.now() < (parseInt(expiresAt) - 300000);
};

export const clearSpotifyToken = () => {
    localStorage.removeItem('spotify_token');
    localStorage.removeItem('spotify_token_expires_at');
};

export const setSpotifyToken = (token, expiresIn = 3600) => {
    localStorage.setItem('spotify_token', token);
    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem('spotify_token_expires_at', expiresAt.toString());
};

const spotifyFetch = async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(`Spotify API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
            error.status = response.status;
            error.spotifyError = errorData.error;

            if (response.status === 401) {
                console.warn('Spotify token expired or invalid');
                clearSpotifyToken();
                error.tokenExpired = true;
            } else if (response.status === 429) {
                const retryAfter = response.headers.get('Retry-After');
                if (retryAfter) {
                    error.retryAfter = parseInt(retryAfter) * 1000;
                }
            }

            throw error;
        }

        return await response.json();
    } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
            const timeoutError = new Error('Spotify API request timeout');
            timeoutError.name = 'AbortError';
            throw timeoutError;
        }

        throw error;
    }
};

export const getSpotifyAuthUrl = () => {
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        response_type: RESPONSE_TYPE,
        scope: SCOPE,
        show_dialog: 'true'
    });

    return `${AUTH_ENDPOINT}?${params.toString()}`;
};

export const getTopArtists = async (timeRange = 'medium_term', limit = 10) => {
    const validTimeRanges = ['short_term', 'medium_term', 'long_term'];
    if (!validTimeRanges.includes(timeRange)) {
        console.warn(`Invalid time range "${timeRange}". Using "medium_term" instead.`);
        timeRange = 'medium_term';
    }

    if (limit < 1 || limit > 50) {
        console.warn(`Invalid limit "${limit}". Must be between 1-50. Using 10 instead.`);
        limit = 10;
    }

    if (!isTokenValid()) {
        const error = new Error('Spotify token is missing or expired');
        error.tokenExpired = true;
        throw error;
    }

    const requestFn = async () => {
        const token = localStorage.getItem('spotify_token');
        const url = `https://api.spotify.com/v1/me/top/artists?time_range=${timeRange}&limit=${limit}`;

        console.debug(`Spotify API Request: GET ${url}`);

        const data = await spotifyFetch(url, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        if (!data.items || !Array.isArray(data.items)) {
            console.warn('Invalid response structure from Spotify API');
            return [];
        }

        return data.items
            .filter(artist => artist?.name)
            .map(artist => artist.name);
    };

    try {
        return await spotifyRateLimiter.makeRequest(requestFn);
    } catch (error) {
        console.error('Error fetching Spotify top artists:', error.message);
        if (error.tokenExpired) {
            throw error;
        }
        return [];
    }
};

export const getSpotifyProfile = async () => {
    if (!isTokenValid()) {
        const error = new Error('Spotify token is missing or expired');
        error.tokenExpired = true;
        throw error;
    }

    const requestFn = async () => {
        const token = localStorage.getItem('spotify_token');
        return await spotifyFetch('https://api.spotify.com/v1/me', {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
    };

    try {
        return await spotifyRateLimiter.makeRequest(requestFn);
    } catch (error) {
        console.error('Error fetching Spotify profile:', error.message);
        throw error;
    }
};

export const getSpotifyRateLimiterStats = () => spotifyRateLimiter.getStats();
